import Title from "../components/ui/Title";
import { buttonVariants } from "../components/ui/button";
import { Link } from "react-router";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { formatDate } from "../lib/helpers/date_helpers";
import { getCouleurPetiteAnnonce, getEtatPetiteAnnonce } from "../lib/helpers/petites_annonces_helpers";
import { Edit2, Newspaper } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import ShowPetiteAnnonceButton from "../components/petites-annonces/ShowPetiteAnnonceButton";
import PayerPetiteAnnonceButton from "../components/petites-annonces/PayerPetiteAnnonceButton";
import { EtatPetiteAnnonce } from "../lib/enums/etat-petite-annonce.enum";
import DeletePetiteAnnonceButton from "../components/petites-annonces/DeletePetiteAnnonceButton";
import { usePetitesAnnonces } from "../lib/hooks/usePetitesAnnonces";
import { cn } from "@/lib/utils";

export default function PetitesAnnoncesPage() {
  const { data: petitesAnnonces } = usePetitesAnnonces();

  return (
    <div className="mx-auto w-full max-w-7xl pb-8">
      <Title>Petites annonces</Title>
      <div className="mb-5 mt-2 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
        L'Abeille de France vous donne la possibilité de publier vos petites annonces dans la revue.
      </div>

      <Link
        to="/petites-annonces/publier"
        className={cn(
          buttonVariants(),
          "mb-7 w-full gap-2 bg-[#173f35] hover:bg-[#23594a] sm:w-auto",
        )}
      >
        <Edit2 className="h-4 w-4" />
        Publier une annonce dans l'Abeille de France
      </Link>

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]">
        <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
            <Newspaper className="h-5 w-5" />
          </div>
          <h2 className="font-bold text-slate-950">Mes petites annonces</h2>
        </div>

        <Table>
          <TableHeader className="hidden bg-slate-50/70 sm:table-header-group">
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[160px] px-6">Date</TableHead>
              <TableHead>Première ligne</TableHead>
              <TableHead className="w-[210px]">Statut</TableHead>
              <TableHead className="px-6 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!petitesAnnonces ? (
              [0, 1, 2].map((item) => (
                <TableRow key={item}>
                  <TableCell colSpan={4} className="px-5 py-4 sm:px-6">
                    <Skeleton className="h-12 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : petitesAnnonces.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="px-5 py-12 text-center text-sm text-slate-500">
                  Vous n'avez pas encore publié d'annonces
                </TableCell>
              </TableRow>
            ) : (
              petitesAnnonces.map((annonce) => (
                <TableRow
                  key={annonce.id}
                  className="block px-4 py-4 hover:bg-slate-50/70 sm:table-row sm:px-0 sm:py-0"
                >
                  <TableCell className="block px-0 pb-1 pt-0 text-sm font-medium text-slate-500 sm:table-cell sm:px-6 sm:py-4 sm:text-slate-700">
                    {formatDate(annonce.createdAt)}
                  </TableCell>
                  <TableCell className="block px-0 py-1 font-semibold text-slate-950 sm:table-cell sm:p-4">
                    {annonce.ligne1}
                  </TableCell>
                  <TableCell className="block px-0 py-2 sm:table-cell sm:p-4">
                    <div
                      className={`${getCouleurPetiteAnnonce(annonce.etat)} w-fit rounded-full px-2.5 py-1 text-xs font-semibold`}
                    >
                      {getEtatPetiteAnnonce(annonce.etat)}
                    </div>
                  </TableCell>
                  <TableCell className="block px-0 pb-0 pt-2 text-right sm:table-cell sm:px-6 sm:py-4">
                    <div className="flex flex-wrap justify-end gap-2">
                      {annonce.etat === EtatPetiteAnnonce.AttentePaiement && (
                        <PayerPetiteAnnonceButton id={annonce.id} />
                      )}
                      <ShowPetiteAnnonceButton initialPetiteAnnonce={annonce} />
                      {annonce.etat === EtatPetiteAnnonce.AttentePaiement && (
                        <DeletePetiteAnnonceButton petiteAnnonce={annonce} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
